import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, message, Tooltip, Popover, Spin, Layout, Avatar, Badge } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined,
  ReloadOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AgentConfigService from '../services/AgentConfigService';
import sessionStorageManager from '../services/sessionStorageManager';
import SessionService from '../services/SessionService';
import { CircleDollarSign, MessageSquareMore, Receipt } from 'lucide-react';
import TabNavigation from './TabNavigation';
import EnvVersionLabel from './Envversionlabel';

const { Title, Text } = Typography;
const { Header, Sider, Content } = Layout;

/**
 * AgentHCCHome - The HCC Agent Workspace page
 * Shows agents specific to HCC Coding
 * 
 * @param {Object} props
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.userRole - Current user's role
 * @param {Array} props.userFeatures - User's available features/tabs
 * @param {function} props.onLogout - Logout handler function
 */
const AgentHCCHome = ({ username, sessionId, userRole, userFeatures, tabOrder, onLogout }) => {
  const navigate = useNavigate();
  
  // State for features
  const [features, setFeatures] = useState(userFeatures || ['HUB', 'COLAB', 'HCC']);
  const [localTabOrder, setLocalTabOrder] = useState(tabOrder || ['HUB', 'HCC', 'COLAB', 'MCP', 'COUNSEL']);

  // State for agent configuration
  const [agentGroups, setAgentGroups] = useState({});
  const [agentConfigs, setAgentConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAgentSessions, setActiveAgentSessions] = useState({});

  const [workspaceTitle, setWorkspaceTitle] = useState('Welcome to the HCC Workspace'); // fallback default


  // For dynamic content fetching
  const [dynamicCardContent, setDynamicCardContent] = useState({});
  
  // For sidebar collapse state
  const [siderCollapsed, setSiderCollapsed] = useState(true);

  
  const getIconCircleStyle = (isDraft = false) => ({
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'white',
    border: `2px solid ${isDraft ? '#9E9E9E' : '#162349'}`,
    color: isDraft ? '#9E9E9E' : '#162349',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  });
  


  // Define a reusable style for icon circles
  const iconCircleStyle = getIconCircleStyle(false);

  /*
  const iconCircleStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'white',
    border: '2px solid #162349',
    color: '#162349',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  };
  */
  

  useEffect(() => {
    // Load workspace title from public directory
    fetch('/title/hcc-title.txt')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load title');
        }
        return response.text();
      })
      .then(text => setWorkspaceTitle(text.trim()))
      .catch(error => {
        console.warn('Could not load workspace title, using default:', error);
        // Keep the default value from state initialization
      });
  }, []);


  useEffect(() => {
    loadHCCAgents();
  }, [sessionId, userRole]);

  // Refresh features on navigation (using cookie cache)
  useEffect(() => {
    const refreshFeatures = async () => {
      console.log('HCC Home - userFeatures:', userFeatures);
      try {
        const mainApiUrl = await AgentConfigService.getMainApiUrl();
        const response = await fetch(`${mainApiUrl}/features`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include'
        });
        
        if (response.ok) {
          const { features: serverFeatures } = await response.json();
          setFeatures(serverFeatures);
        }

        // Update local tab order if provided
        if (response.tab_order) {
          setLocalTabOrder(response.tab_order);
        }

      } catch (error) {
        console.warn('Failed to refresh features, using fallback:', error);
        setFeatures(['HUB', 'COLAB', 'HCC']);
      }
    };
    
    if (sessionId && !userFeatures) {
      refreshFeatures();
    }
  }, [sessionId, userFeatures]);


  useEffect(() => {
    // Load active agent sessions
    const activeSessions = sessionStorageManager.getActiveAgentSessions(sessionId);
    setActiveAgentSessions(activeSessions);
  }, [sessionId]);


  

  /**
   * Load HCC agents from unified config
   * Reads the "HCC" key from the role's configuration
   */
  const loadHCCAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to load from unified config first
      let rcmAgentMap = {};
      try {
        // Load unified config
        const unifiedConfig = await AgentConfigService.loadUnifiedConfig(sessionId);
        console.log("Loaded unified config:", unifiedConfig);
        
        // Get HCC agents for current role
        const roleConfig = unifiedConfig.config || {};

        rcmAgentMap = roleConfig['HCC'] || {};
        console.log("HCC agent mapping for role:", rcmAgentMap);
        
      } catch (error) {
        console.error("Failed to load unified config, trying fallback:", error);
        
        // Fallback: try role_agent_config with HCC key
        try {
          const roleAgentMap = await AgentConfigService.loadRoleAgentConfig();
          const roleData = roleAgentMap[userRole] || {};
          
          // Check if there's an HCC key in the old config
          if (roleData['HCC']) {
            rcmAgentMap = roleData['HCC'];
          }
        } catch (fallbackError) {
          console.error("Fallback also failed:", fallbackError);
          setError("Failed to load HCC agent configuration.");
        }
      }
      
      // Load all agent configs (card details)
      let allAgentConfigs;
      try {
        allAgentConfigs = await AgentConfigService.loadAllAgentConfig();
        console.log("Loaded agent configurations:", allAgentConfigs);
      } catch (error) {
        console.error("Failed to load agent configurations:", error);
        setError("Failed to load agent configurations. Using default agents.");
        
        // Use fallback configuration
        allAgentConfigs = {
          "agent100001": {
            "id": "agent100001",
            "name": "Eligibility Agent",
            "icon": "window-s4.png",
            "endpoint": "http://localhost:8011",
            "outcome_text": "All Possible Outcome"
          },
          "agent100002": {
            "id": "agent100002", 
            "name": "Service Auth Check Agent",
            "icon": "check-s4.png", 
            "endpoint": "http://localhost:8012",
            "outcome_text": "Medical Policy Criteria Check"
          }
        };
      }
      
      setAgentConfigs(allAgentConfigs);
      
      // Process agent groups
      const groups = {};
      
      // Process each group based on the HCC agent mapping
      try {
        Object.entries(rcmAgentMap).forEach(([groupName, agentIds]) => {
          if (Array.isArray(agentIds)) {
            const agentsInGroup = agentIds
              .map(id => {
                const agent = allAgentConfigs[id];
                if (agent) {
                  return agent;
                }
                return null;
              })
              .filter(Boolean);
            
            if (agentsInGroup.length > 0) {
              groups[groupName] = agentsInGroup;
            }
          }
        });
        
        setAgentGroups(groups);
        console.log("Processed HCC agent groups:", groups);
      } catch (error) {
        console.error("Error processing HCC agent groups:", error);
        setError("Error processing agent groups.");
      }
      
    } catch (error) {
      console.error("General error in loadHCCAgents:", error);
      setError("Failed to load HCC agent configuration. Please try refreshing the page.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    await SessionService.logout(sessionId, onLogout);
  };

  // Handle clicking on an agent card
  const handleAgentCardClick = (agent) => {

    if (agent.agent_type === 'HCC_Inventory') {
      navigate(`/my-inventory/charts`);
    } 
    // Check for V3 (generic layout, no chat)
    else if (agent.chat_version === 'V3') {
      navigate(`/hcc-home/agent/v3/${agent.id}`);  // or `/hub/agent/v3/...`
    }
    // Check for V2 chat component support
    else if (agent.chat_version === 'V2') {
      navigate(`/hcc-home/agent/v2/${agent.id}`);
    } 
    // Default to V1 route
    else {
      navigate(`/hcc-home/agent/${agent.id}`);
    }
  };
  

  // Helper function to determine the correct icon path
  const getIconPath = (agent) => {
    // Check if this is a user-created agent (ID starts with 'user_agent_')
    if (agent.id && agent.id.startsWith('user_agent_')) {
      return `/resources/user_agent_icons/${agent.icon || 'default.png'}`;
    }
    // Default path for system agents
    return `/resources/icons/${agent.icon || 'default.png'}`;
  };

  // Handle refresh configuration
  const handleRefreshConfig = async () => {
    try {
      // Clear all config caches
      localStorage.removeItem('agent_config');
      localStorage.removeItem('role_agent_config');
      localStorage.removeItem('common_config');
      localStorage.removeItem('unified_config');
      
      // Show loading message
      message.info('Refreshing configuration...');
      
      // Reload the data
      await loadHCCAgents();
      
      message.success('Configuration refreshed!');
    } catch (error) {
      console.error('Error refreshing configuration:', error);
      message.error('Failed to refresh configuration');
    }
  };


  const isDraftOrUpcoming = (agent) => {
    if (!agent.version) return false;
    const draftKeywords = ['draft', 'upcoming', 'planned', 'roadmap', 'future'];
    const versionLower = agent.version.toLowerCase();
    return draftKeywords.some(keyword => versionLower.includes(keyword));
  };



  // Render agent card with dynamic and static content
  const renderAgentCard = (agent) => {
    console.log('Agent: ', agent);
    
    // Check if this agent has an active session
    const hasActiveSession = activeAgentSessions[agent.id];
    
    // Check if agent is draft/upcoming
    const isDraft = isDraftOrUpcoming(agent);

    // Get icon data from agent configuration or use defaults
    const cardIcons = agent.card_icons || [
      { letter: 'i', content: 'Detailed Information about functionality' },
      { letter: 'v', content: `Version: ${agent.version || '1.0.0'}` },
      { letter: 'r', content: agent.help_text || 'No release notes available' },
    ];
    
    // Add dynamic content if available
    if (dynamicCardContent[agent.id]) {
      const dynamicContent = dynamicCardContent[agent.id];
      
      if (dynamicContent.usage) {
        cardIcons.push({ letter: 'u', content: dynamicContent.usage });
      }
      
      if (dynamicContent.performance) {
        cardIcons.push({ letter: 'p', content: dynamicContent.performance });
      }
      
      if (dynamicContent.feedback) {
        cardIcons.push({ letter: 'f', content: dynamicContent.feedback });
      }
    }
    
    // Define colors based on draft status
    const textColor = isDraft ? '#9E9E9E' : undefined;
    const iconBorderColor = isDraft ? '#9E9E9E' : '#162349';
    
    return (
      <Card
        hoverable={!isDraft}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          cursor: isDraft ? 'not-allowed' : 'pointer',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        onClick={isDraft ? undefined : () => handleAgentCardClick(agent)}
      >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <Title 
            level={4} 
            style={{ 
              margin: 0, 
              fontSize: '18px',
              color: isDraft ? '#9E9E9E' : undefined
            }}
          >
            {agent.name}
          </Title>
          {hasActiveSession && (
            <Tooltip title="Active session with unsaved data">
              <Badge 
                dot 
                status="processing"
                style={{ marginLeft: '8px' }}
              >
                <MessageSquareMore 
                  size={24} 
                  color={isDraft ? '#9E9E9E' : '#162349'}
                  style={{ display: 'block' }}
                />
              </Badge>
            </Tooltip>
          )}
        </div>
        <div style={{ marginLeft: '12px', flexShrink: 0 }}>
          <img
            src={`${getIconPath(agent)}?v=${Date.now()}`}
            alt={agent.name}
            style={{ 
              width: '64px',
              height: '64px',
              objectFit: 'contain',
              borderRadius: '50%',
              backgroundColor: '#f5f5f5',
              filter: isDraft ? 'grayscale(100%)' : 'none',
              opacity: isDraft ? 0.6 : 1
            }}
            onError={(e) => {
              e.target.src = '/resources/icons/default.png';
            }}
          />
        </div>
      </div>
      <Text 
        type="secondary" 
        style={{ 
          fontSize: '14px',
          color: isDraft ? '#9E9E9E' : undefined,
          display: 'block',
          marginBottom: '12px'
        }}
      >
        {agent.description || 'No description available'}
      </Text>
        
        <div style={{ marginTop: 'auto' }}>
          {/* Display two rows of icons, up to 4 per row */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {cardIcons.slice(0, 4).map((icon, index) => (
                <Popover
                  key={index}
                  content={<div style={{ maxWidth: '300px' }}>{icon.content}</div>}
                  title={`${icon.letter.toUpperCase()} `}
                  trigger="hover"
                >
                  <div style={getIconCircleStyle(isDraft)}>
                    {icon.letter}
                  </div>
                </Popover>
              ))}
            </div>
            
            {cardIcons.length > 4 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {cardIcons.slice(4, 8).map((icon, index) => (
                  <Popover
                    key={index}
                    content={<div style={{ maxWidth: '300px' }}>{icon.content}</div>}
                    title={`${icon.letter.toUpperCase()} `}
                    trigger="hover"
                  >
                    <div style={getIconCircleStyle(isDraft)}>
                      {icon.letter}
                    </div>
                  </Popover>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };


  

  
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading HCC Agent Workspace...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#f5222d' }}>
        <div style={{ fontSize: '18px', marginBottom: '8px' }}>Error</div>
        <div>{error}</div>
        <Button onClick={loadHCCAgents} style={{ marginTop: '16px' }}>
          Try Again
        </Button>
      </div>
    );
  }
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar */}
      <Sider 
        width={60} 
        collapsible 
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div style={{ 
          display: 'flex', 
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          height: '100%',
          alignItems: 'center',
          padding: '20px 0'
        }}>
          {/* Top icons would go here */}
          <div></div>
          
          {/* Bottom user and logout icons - MINIMAL HCC HUB SIDEBAR */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            

            <Tooltip title={username} placement="right">
              <Avatar 
                style={{ 
                  backgroundColor: 'white', 
                  color: '#162349', 
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />} 
              />
            </Tooltip>
            
            <Tooltip title="Logout" placement="right">
              <Button 
                type="text" 
                icon={<LogoutOutlined style={{ color: 'white' }} />} 
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: "Bold" }} />
          </div>
        </div>
      </Sider>
      
      <Layout style={{ marginLeft: '60px' }}>
        {/* Top Header */}
        <Header style={{ 
          background: '#162349', 
          padding: '8px 24px 0 24px',
          height: 'auto',
          color: 'white'
        }}>
          {/* Onaxis Brand and Tab Navigation */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0' }}>
            <TabNavigation 
              features={features}
              activeTab="HCC"
              tabOrder={localTabOrder}
              onRefresh={handleRefreshConfig}
              refreshing={false}
            />
          </div>
        </Header>

        {/* Main Content */}
        <Content style={{ 
          padding: '24px', 
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)'
        }}>

          {/* Welcome Header */}
           <Title level={1} style={{ marginBottom: '40px', fontSize: '28px'}}>
            {workspaceTitle}
          </Title>

          {/* HCC Agent Categories */}
          {Object.keys(agentGroups).length > 0 ? (
            Object.entries(agentGroups).map(([groupName, groupAgents]) => (
              <div key={groupName} style={{ marginBottom: '32px' }}>
                <Title level={2} style={{ marginBottom: '16px', fontSize: '22px' }}>
                  {groupName}
                </Title>
                
                <Row gutter={[24, 24]}>
                  {groupAgents.map(agent => (
                    <Col key={agent.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                      {renderAgentCard(agent)}
                    </Col>
                  ))}
                </Row>
              </div>
            ))
          ) : (
            <div style={{ 
              padding: '60px', 
              textAlign: 'center',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <Receipt size={64} color="#162349" style={{ marginBottom: '16px' }} />
              <Title level={3} style={{ color: '#666' }}>No HCC Agents Available</Title>
              <Text type="secondary">
                No HCC agents are configured for your role. Please contact your administrator.
              </Text>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AgentHCCHome;